/* eslint-disable no-nested-ternary */
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { Fragment, memo, useEffect, useState } from "react";
import styles from "./TenancyCard.module.scss";
import InfoBlock from "./components/InfoBlock";
import useRoomTypeContext from "@Pages/PropertyPage/components/RoomTypeSection/context/useRoomTypeContext";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import gtagEventEmitter, { gtagDuplicateEventEmitter } from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import TenancyCTA from "../TenancyCTA";
import getPaymentFormLink from "@Pages/PropertyPage/components/PropertyCTAs/utils/getPaymentFormLink";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES, HAPPY_FLOW_TENANCY_CTAS } from "constants/index";
import getBookFormLink from "@Pages/PropertyPage/components/PropertyCTAs/utils/getBookFormLink";
import { updatePropertyData } from "@App/userHistoryReducer";
import { useHistory } from "hooks/useHistory";
import useIsDesktop from "hooks/useIsDesktop";
import getActionInitiatedData from "@Components/LeadFormV2/utils/getActionInitiatedData";
import viewTransitionNavUtility from "@Utils/viewTransitionNavUtility";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import useIsHappyFlowBookForm from "hooks/useIsHappyFlowBookForm";
import isPaymentActive from "@Utils/isPaymentActive";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import { calculateDiscountedPrice } from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import PriceDisplay from "./components/PriceDisplay";
import getCookie from "helpers/getCookie";
import setCookie from "helpers/setCookie";
import config from "@Config/index";
import useIsApp from "hooks/useIsApp";
import { isWhitelabelPartnerInventoryFunc } from "@Utils/whitelabelHelper";
import useCustomWindowOpen from "@Components/CustomWindowOpen";

const DATA_BLOCKS_DESKTOP = (t: any) => [
  { label: t("propertypage.components.tenancycard.duration"), key: "duration" },
  { label: t("propertypage.components.tenancycard.moveInDate"), key: "moveInDate" },
  { label: t("propertypage.components.tenancycard.moveOutDate"), key: "moveOutDate" },
  { label: t("propertypage.components.tenancycard.area"), key: "area" },
  { label: t("propertypage.components.tenancycard.floor"), key: "floor" },
  { label: "View", key: "facing" },
];

const DATA_BLOCKS_MOBILE_1 = (t: any) => [
  { label: t("propertypage.components.tenancycard.duration"), key: "duration" },
  { label: t("propertypage.components.tenancycard.moveInDate"), key: "moveInDate" },
  { label: t("propertypage.components.tenancycard.moveOutDate"), key: "moveOutDate" },
];

const DATA_BLOCKS_MOBILE_2 = [
  { label: "Area", key: "area" },
  { label: "View", key: "facing" },
  { label: "Floor", key: "floor" },
];

type TenancyCardProps = {
  isSoldOut?: boolean;
  name?: string;
  duration?: string | null;
  moveInDate?: string;
  moveOutDate?: string;
  price?: string;
  tenancy?: any;
  note?: any;
  floor?: string;
  facing?: string;
  bookHandler: any;
  handleWaitlist: any;
  activeTab: string;
  roomId: number;
  roomTypeData?: any;
  isInsideModal?: boolean;
};

type dataBlockValuesType = {
  name?: string;
  duration?: any;
  moveInDate?: any;
  moveOutDate?: any;
  tenancy?: any;
  note?: any;
  floor?: any;
  facing?: any;
};

const TenancyCard = ({
  name,
  duration,
  moveInDate,
  moveOutDate,
  tenancy,
  price,
  note,
  floor,
  facing,
  isSoldOut = false,
  bookHandler,
  handleWaitlist,
  activeTab = "",
  roomId,
  roomTypeData,
  isInsideModal,
}: TenancyCardProps) => {
  const customWindowOpen = useCustomWindowOpen();
  const { isWhitelabel, isDemoSubdomain, partner_uuid, partner_id } = useWhitelabel();
  const { propertyData } = useRoomTypeContext();

  const isWhitelabelPartnerInventory = isWhitelabelPartnerInventoryFunc(propertyData, partner_id);
  const {
    CONTINUE_BOOKING,
    CONTINUE_PAYMENT,
    VIEW_BOOKINGS,
    JOIN_WAITLIST,
    BOOK,
    APPLY_NOW,
    CONTINUE_APPLY,
  } = HAPPY_FLOW_TENANCY_CTAS;
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const history = useHistory();
  const isDesktop = useIsDesktop();
  const { isHMO } = useRoomTypeContext();
  const isHappyFlow = useIsHappyFlow();
  const isHappyFlowBookForm = useIsHappyFlowBookForm();
  const DATA_BLOCKS = isDesktop ? DATA_BLOCKS_DESKTOP(t) : DATA_BLOCKS_MOBILE_1(t);

  const { personalDetails, rooms, inventoriesData } = useSelector(
    ({ leadFormModal, propertyPageData, userHistory }: AppState) => ({
      personalDetails: leadFormModal.personalDetails,
      rooms: propertyPageData.roomTypes.data,
      inventoriesData: userHistory.inventoriesData,
    }),
    shallowEqual,
  );
  const isPaymentBeforeBookform =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ENABLE_PAYMENT_BEFORE_BF) &&
    propertyData?.payment_before_bookform &&
    isPaymentActive(propertyData);
  const hasToShowSlashedPricing = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SLASHED_PRICING);
  const showBillsIncluded =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_BILLS_INCLUDED) || config.IS_CHINA_APP;
  const isApplyNowCTA = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.APPLY_NOW_CTA);
  const isApp = useIsApp();

  const dataBlockValues: dataBlockValuesType = {
    name,
    duration,
    moveInDate,
    moveOutDate,
    tenancy,
    note,
    floor,
    facing,
  };

  const [slashedPriceState, setSlashedPriceState] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleEnquire = (ctaText: string, redirectionLink: string, fromCardClick = false) => {
    const paymentBeforeBookformParam =
      isPaymentBeforeBookform &&
      !(redirectionLink && redirectionLink.includes("payment_before_bookform=true"))
        ? "&payment_before_bookform=true"
        : "";

    if (ctaText === BOOK) {
      gtagDuplicateEventEmitter({
        action: "clicked_book_tenancy_room_newleadform",
        category: PAGE.INVENTORY,
        label: ` ${name}, ${duration}`,
        tenancy_id: tenancy?.id,
        active_tab: activeTab,
        fromCardClick,
      });
      gtagEventEmitter({
        action: "clicked_book_tenancy_room",
        category: PAGE.INVENTORY,
        label: ` ${name}, ${duration}`,
        active_tab: activeTab,
        fromCardClick,
      });
      bookHandler(tenancy, "Book Now", true, roomId);
    } else if (ctaText === APPLY_NOW) {
      gtagDuplicateEventEmitter({
        action: "clicked_apply_tenancy_room_newleadform",
        category: PAGE.INVENTORY,
        label: ` ${name}, ${duration}`,
        tenancy_id: tenancy?.id,
        active_tab: activeTab,
        fromCardClick,
      });
      gtagEventEmitter({
        action: "clicked_apply_tenancy_room",
        category: PAGE.INVENTORY,
        label: ` ${name}, ${duration}`,
        active_tab: activeTab,
        fromCardClick,
      });
      bookHandler(tenancy, "Apply Now", true, roomId);
    } else if (ctaText === CONTINUE_BOOKING || ctaText === CONTINUE_APPLY) {
      gtagEventEmitter({
        action: "clicked_continue_booking_tenancy",
        category: PAGE.INVENTORY,
        label: ` ${name}, ${duration}`,
        active_tab: activeTab,
        fromCardClick,
      });
      dispatch(
        updatePropertyData({
          inventoryId: propertyData.id,
          book_form_initiated: getActionInitiatedData({
            inventory: tenancy ?? roomTypeData,
            label: t(HAPPY_FLOW_TENANCY_CTAS.CONTINUE_BOOKING),
            isTenancy: Boolean(tenancy),
          }),
        }),
      );
      const link = redirectionLink
        ? `${redirectionLink[0] === "/" ? "" : "/"}${redirectionLink}`
        : getBookFormLink({
            rooms,
            id: tenancy?.id ? tenancy.id : roomId,
            canonicalName: propertyData.canonical_name,
          });
      if (isHappyFlowBookForm)
        viewTransitionNavUtility(
          () =>
            history.push(
              isWhitelabel
                ? convertToWhitelabelURL(
                    `${link}${paymentBeforeBookformParam}`,
                    isDemoSubdomain,
                    partner_uuid,
                  )
                : `${link}${paymentBeforeBookformParam}`,
            ),
          "right-to-left",
        );
      else
        customWindowOpen({
          url: isWhitelabel
            ? convertToWhitelabelURL(
                `${link}${paymentBeforeBookformParam}`,
                isDemoSubdomain,
                partner_uuid,
              )
            : `${link}${paymentBeforeBookformParam}`,
          target: "_self",
        });
    } else if (ctaText === CONTINUE_PAYMENT) {
      gtagEventEmitter({
        action: "clicked_continue_payment_tenancy",
        category: PAGE.INVENTORY,
        label: ` ${name}, ${duration}`,
        active_tab: activeTab,
        fromCardClick,
      });
      dispatch(
        updatePropertyData({
          inventoryId: propertyData.id,
          payment_initiated: getActionInitiatedData({
            inventory: tenancy ?? roomTypeData,
            label: t(HAPPY_FLOW_TENANCY_CTAS.CONTINUE_PAYMENT),
            isTenancy: Boolean(tenancy),
          }),
        }),
      );
      const link = redirectionLink
        ? `${redirectionLink[0] === "/" ? "" : "/"}${redirectionLink}`
        : getPaymentFormLink({
            rooms,
            id: tenancy?.id ? tenancy.id : roomId,
            canonicalName: propertyData.canonical_name,
          });
      const targetLink = isWhitelabel
        ? convertToWhitelabelURL(
            `${link}${paymentBeforeBookformParam}`,
            isDemoSubdomain,
            partner_uuid,
          )
        : `${link}${paymentBeforeBookformParam}`;

      if (isHappyFlowBookForm) {
        viewTransitionNavUtility(() => history.push(targetLink), "right-to-left");
      } else {
        customWindowOpen({ url: targetLink, target: "_self" });
      }
    } else if (ctaText === VIEW_BOOKINGS) {
      const targetUrl = isWhitelabel
        ? convertToWhitelabelURL("/profile/leads", isDemoSubdomain, partner_uuid)
        : "/profile/leads";
      if (isApp) history.push("/profile/leads");
      else customWindowOpen({ url: targetUrl, target: isDesktop ? "_self" : "_blank" });
    }
  };

  const handleCardClick = (ctaText: any, link: any) => {
    if (!isSoldOut && !isHMO) {
      onBookNowClick(ctaText || "", link, true);
    }
  };

  const handleOnClickPriceTag = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setShowModal(true);
  };

  const onBookNowClick = (ctaText: string, link: string, fromCardClick = false) => {
    const redirectDirectlyFlag = false; // Manual trigger since this was controlled by feature flag
    if (redirectDirectlyFlag && !(Object.keys(personalDetails).length === 0)) {
      // handleBook();
    } else {
      handleEnquire(ctaText, link, fromCardClick);
    }
  };

  const { ctaText, link }: any = (() => {
    if (
      isHappyFlow &&
      Object.keys(inventoriesData.children).length > 0 &&
      inventoriesData.children[String(roomId)] &&
      !isWhitelabelPartnerInventory
    ) {
      const tenancies = inventoriesData.children[String(roomId)].children;
      if (tenancy?.id && Object.keys(tenancies).length > 0 && tenancies[tenancy?.id]) {
        const selectedTenancy =
          inventoriesData.children[String(roomId)].children[String(tenancy.id)];
        return {
          ctaText:
            isApplyNowCTA && selectedTenancy.label !== VIEW_BOOKINGS
              ? selectedTenancy.label === BOOK
                ? APPLY_NOW
                : selectedTenancy.label?.replace(/Book/g, "Apply")
              : selectedTenancy.label,
          link: selectedTenancy.link,
        };
      }
      if (!tenancy?.id) {
        const selectedRoom = inventoriesData.children[String(roomId)];
        return {
          ctaText:
            isApplyNowCTA && selectedRoom?.label !== VIEW_BOOKINGS
              ? selectedRoom?.label === BOOK
                ? APPLY_NOW
                : selectedRoom?.label?.replace(/Book/g, "Apply")
              : selectedRoom.label,
          link: selectedRoom.link,
        };
      }
    }
    return {
      // eslint-disable-next-line no-nested-ternary
      ctaText: isSoldOut ? JOIN_WAITLIST : isApplyNowCTA ? APPLY_NOW : BOOK,
      link: null,
    };
  })();

  const getSubContainerMobile = () => {
    let string = "";
    DATA_BLOCKS_MOBILE_2.forEach(({ label, key }) => {
      const blockValue = dataBlockValues?.[key as keyof dataBlockValuesType];
      if (blockValue) {
        string += `${label}: ${blockValue} ${" | "}`;
      }
    });
    return string;
  };

  const slashedPrice = calculateDiscountedPrice({
    tenancy,
    price,
    propertyData,
    duration,
    roomTypeData,
    isDesktop,
    showBillsIncluded,
  });

  useEffect(() => {
    const slashedPriceCookie = getCookie("slash_pricing") || "false";
    if (slashedPrice.offers_array?.length) {
      if (JSON.parse(slashedPriceCookie)) setSlashedPriceState(true);
      else if (hasToShowSlashedPricing) {
        setCookie({ cname: "slash_pricing", cvalue: true });
        setSlashedPriceState(true);
      }
    }
  }, []);

  return (
    <div
      className={styles.cardContainer}
      role="none"
      onClick={() => handleCardClick(ctaText, link)}
    >
      <div className={styles.sectionContainer}>
        <div className={formatClassNames(styles.leftSection, isDesktop && styles.desktop)}>
          {(isSoldOut ? DATA_BLOCKS.slice(0, 3) : DATA_BLOCKS).map(({ label, key }) => {
            const blockValue = dataBlockValues?.[key as keyof dataBlockValuesType];
            if (blockValue) {
              return (
                <InfoBlock
                  testId={`property-room-${label}-value`}
                  key={label}
                  label={label}
                  value={blockValue}
                  isBold={key === "duration" && isDesktop}
                  disabled={isSoldOut}
                  isDesktop={isDesktop}
                  hideTitle={false}
                />
              );
            }

            return <Fragment key={key} />;
          })}
          {Boolean(note) && !isSoldOut && isDesktop && (
            <div className={formatClassNames(styles.note, isDesktop && styles.desktop)}>
              <span>
                {t("propertypage.roomtypesectiondynamic.roomtypecard.note")}: {t(note)}
              </span>
            </div>
          )}
        </div>

        <div className={formatClassNames(styles.rightSection, isDesktop && styles.desktop)}>
          <div className={styles.priceContainer}>
            {isSoldOut && isDesktop && (
              <span
                data-testid="property-room-type-tenancy-soldout"
                className={formatClassNames(styles.soldOutBadge, isDesktop && styles.desktop)}
              >
                {t("propertypage.roomtypesectiondynamic.roomtypecard.soldout")}
              </span>
            )}
            <PriceDisplay
              price={price}
              slashedPrice={slashedPrice}
              isSoldOut={isSoldOut}
              onPriceClick={handleOnClickPriceTag}
              showModal={showModal}
              onCloseModal={(e: Event) => {
                e?.stopPropagation();
                setShowModal(false);
              }}
              isDesktopModal={isInsideModal}
              slashedPriceState={slashedPriceState}
            />
          </div>
          <div onClick={() => handleCardClick(ctaText, link)} role="none">
            <TenancyCTA
              roomId={roomId}
              tenancy={tenancy}
              isSoldOut={isSoldOut}
              handleWaitlist={handleWaitlist}
              onBookNowClick={onBookNowClick}
              ctaText={ctaText || ""}
              link={link || ""}
            />
          </div>
        </div>
      </div>
      {!isDesktop && getSubContainerMobile() && (
        <div className={styles.subContainer}>{getSubContainerMobile()}</div>
      )}

      {Boolean(note) && !isSoldOut && !isDesktop && (
        <div className={styles.note}>
          {t("propertypage.roomtypesectiondynamic.roomtypecard.note")}: {t(note)}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(memo(TenancyCard), {
  componentName: "TenancyCard-RoomTypeCard-RoomTypeSection-PropertyPage",
  showForChina: true,
});
